import { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    runModel as runModelService,
    elecCorrect as elecCorrectService,
} from '../../services/model';
import {
    runFertilizerModel as runFertilizerModelService,
    runBalanceAcidBase as runBalanceAcidBaseService,
} from '../../services/fertilizer';
import ModelViewComponent from './ModelViewComponent';
import { formatObjectValues, formatObjectValuesDeep1 } from './utils';
import useStore, { actions } from '../../store/useStore';
import { getStreefwaarden } from '../../store/reducer';
import {
    formatIonInputValue,
    reuseDrainwaterPossible,
} from '../../store/helpers';
import validate from './validate';
import { modelVersions, drainReuseKeys } from '../../services/constants';
import { loadingStates } from '../../store/constants';
import { guestKey } from '../../services/constants';
import Panel from '../Panel';
import { changeKey } from '../../store/asyncActions';
import { filterObject } from '../../helpers/updateState';

const Button = styled.button``;

const ModelViewController = ({ modelVersion }) => {
    const { store, dispatch } = useStore();
    const [fertilizerViewOpen, setFertilizerViewOpen] = useState(false);

    const ionParameters = store.config?.ionParameters;

    const [busy, setBusy] = useState(false);

    const {
        selectedCropParameter,
        selectedSubstrate,
        selectedStage,
        reuseDrainwaterValue,
        chlorineValue,
        summerWinter,
        ureumValue,
        dropWaterValue,
        ionValues,
        useDrainAnalysis,
        useDefaultValues,
    } = store.input ?? {};

    const {
        modelOutput,
        modelOutputElecCorrect,
        modelOutputFertilizer,
        modelOutputFertilizerBalanced,
        fieldErrors,
        outputColumns,
        showElecCorrect,
    } = store;

    // (re)set selected substrate when crop changes:
    useEffect(() => {
        dispatch(actions.changeCropEffect());
        // dispatch(
        //     actions.setInputValue('selectedSubstrate', (prev) => {
        //         // if only one substrate for crop, select it:
        //         if (selectedCropParameter?.substrates?.length === 1) {
        //             return selectedCropParameter.substrates[0];
        //         }
        //         // reset selected substrate
        //         return null;
        //     })
        // );

        // // @todo: check where this is in new config:
        // dispatch(actions.setInputValue('summerWinter', ''));
    }, [selectedCropParameter, dispatch]);

    // When changing substrate, check if drain reuse is possible
    // if default (drain) values are used, reset values and option
    useEffect(() => {
        if (!selectedSubstrate?.drainReuse) {
            dispatch(
                actions.setInputValue('reuseDrainwaterValue', (prev) => ({
                    ...prev,
                    reuse: false,
                }))
            );
        }
        if (useDefaultValues) {
            dispatch(actions.changeSubstrateEffect());
        }
    }, [selectedSubstrate, dispatch]);

    // (re)set selected stage when changing substrate or reuseDrainwaterValue
    useEffect(() => {
        if (modelVersion === modelVersions.potplant) {
            return;
        }

        dispatch(
            actions.setInputValue('selectedStage', (prev) => {
                if (!selectedSubstrate) {
                    return null;
                }
                // if only one stage for substrate, select it:
                const draintypeConfig =
                    selectedSubstrate.draintypeConfig[
                        reuseDrainwaterValue?.reuse
                            ? drainReuseKeys.reuse
                            : drainReuseKeys.free
                    ];
                const phases = draintypeConfig?.phases;
                if (!phases) {
                    return null;
                }
                if (phases?.length === 1) {
                    return draintypeConfig.phases[0];
                }
                if (phases.includes(prev)) {
                    return prev;
                }
                // reset selected stage
                return null;
            })
        );
        // dispatch(actions.clearIonValues());
    }, [selectedSubstrate, reuseDrainwaterValue, dispatch, modelVersion]);

    useEffect(() => {
        if (!modelOutput) {
            setFertilizerViewOpen(false);
        }
    }, [modelOutput]);

    const switchToAnonymous = () => {
        changeKey(store, dispatch, guestKey);
    };

    if (
        store.loadingState === loadingStates.initializing ||
        store.loadingState === loadingStates.fetching
    ) {
        return 'loading...';
    }
    if (store.loadingState === loadingStates.error) {
        return (
            <Panel style={{ maxWidth: 600 }}>
                {Boolean(store.key && store.key !== guestKey) && (
                    <div>
                        <p>
                            Het lijkt erop dat de licentie key die u heeft
                            ingevoerd ongeldig is.
                        </p>
                        <p>De configuratie files konden niet worden geladen.</p>
                        {/* <Button onClick={switchToAnonymous}>
                            Wissel naar anonieme modus
                        </Button> */}
                    </div>
                )}
                {Boolean(!store.key || store.key === guestKey) && (
                    <div>
                        <p>
                            Klik op het sleutel-icoon rechtsboven en vul een
                            geldige gebruikerscode in.
                        </p>
                        {/* <Button onClick={switchToAnonymous}>
                            Wissel naar anonieme modus
                        </Button> */}
                    </div>
                )}

                {store.loadingError?.message && (
                    <pre>{store.loadingError.message}</pre>
                )}
            </Panel>
        );
    }

    const handleChangeIonValue = (ion, param, value) => {
        dispatch(actions.setIonValueInput(ion, param, value));
    };

    const runModel = () => {
        const forValidation = {
            crop: selectedCropParameter?.crop,
            dropWaterValue,
            ionValues,
        };

        if (modelVersion === modelVersions.substraat) {
            forValidation.stage = selectedStage;
            forValidation.substrate = selectedSubstrate;
        }

        // @todo
        if (modelVersion === modelVersions.potplant) {
            if (selectedCropParameter?.cropStages?.length) {
                forValidation.teeltfase = selectedStage;
            }
            forValidation.ureum = ureumValue;
        }

        const validation = validate(forValidation, modelVersion);

        dispatch(actions.setFieldErrors(validation));

        if (!Object.values(validation).length) {
            let empty = Object.values(ionValues).every((values) =>
                Object.values(values).every((val) => !val)
            );
            let nextIonValues = ionValues;

            if (empty) {
                const streefwaarden = getStreefwaarden(store);

                if (streefwaarden) {
                    nextIonValues = Object.entries(streefwaarden).reduce(
                        (accumulator, [ionKey, streefValue]) => {
                            return {
                                ...accumulator,
                                [ionKey]: {
                                    ...ionValues[ionKey],
                                    drainAnalysis: formatIonInputValue(
                                        streefValue.toString()
                                    ),
                                },
                            };
                        },
                        ionValues
                    );
                    dispatch(actions.setInputValue('ionValues', nextIonValues));
                    empty = false;
                }
            }

            if (empty) {
                dispatch(
                    actions.setFieldErrors({
                        ionValues:
                            'Fout: Kan geen standaard streefwaarden vinden',
                    })
                );
            } else {
                setBusy(true);
                runModelService(modelVersion, store.key, {
                    crop: selectedCropParameter.crop,
                    stage: selectedStage,
                    substrate: selectedSubstrate?.substrate,
                    reuseDrainwater: {
                        ...reuseDrainwaterValue,
                        reuse:
                            reuseDrainwaterValue.reuse &&
                            reuseDrainwaterPossible({
                                modelVersion,
                                selectedCropParameter,
                                selectedSubstrate,
                            }),
                    },
                    chlorine: Boolean(
                        selectedSubstrate?.chlorine && chlorineValue
                    ),
                    ureum: ureumValue,
                    dropWaterValue: dropWaterValue,
                    // @todo check where summerWinter param is
                    summerWinter: selectedCropParameter.summerWinterAvailable
                        ? summerWinter
                        : '',
                    ionValues: nextIonValues,
                    useDrainAnalysis,
                })
                    .then((result) => {
                        if (result.error) {
                            dispatch(
                                actions.setFieldErrors({
                                    result: `Server fout: ${result.error}`,
                                })
                            );
                        } else {
                            dispatch(
                                actions.setModelOutput(
                                    formatObjectValuesDeep1(result)
                                )
                            );
                        }
                    })
                    .catch((error) => {
                        dispatch(
                            actions.setFieldErrors({
                                result: `Fout: ${error.message}`,
                            })
                        );
                    })
                    .finally(() => {
                        setBusy(false);
                    });
            }
        }
    };

    const runElecCorrect = () => {
        const ec = modelOutput.advies.EC;
        setBusy(true);
        dispatch(actions.setFieldErrors(null));
        elecCorrectService(modelVersion, store.key, {
            dropWaterValue: ec,
            ionValues,
        })
            .then((result) => {
                dispatch(
                    actions.setModelOutputElecCorrect(
                        formatObjectValues(result)
                    )
                );
            })
            .catch((error) => {
                console.warn('error:', error);
                dispatch(
                    actions.setFieldErrors({
                        elecCorrect: `Fout: ${error.message}`,
                    })
                );
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const runFertilizerModel = (fertilizerSettings, recipe) => {
        const filteredRecipe = filterObject(recipe, (val, key) => {
            return ionParameters.includes(key);
        });
        setBusy(true);
        dispatch(actions.setFieldErrors(null));
        return runFertilizerModelService(
            store.key,
            fertilizerSettings,
            filteredRecipe
        )
            .then((modelResult) => {
                dispatch(actions.setModelOutputFertilizer(modelResult));
            })
            .catch((error) => {
                console.warn('error:', error);
                dispatch(
                    actions.setFieldErrors({
                        fertilizerModel: `Fout: ${error.message}`,
                    })
                );
                dispatch(actions.setModelOutputFertilizer(null));
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const runBalanceAcidBase = (modelOutputFertilizer) => {
        setBusy(true);
        dispatch(actions.setFieldErrors(null));
        runBalanceAcidBaseService(store.key, modelOutputFertilizer)
            .then((modelResult) => {
                dispatch(actions.setModelOutputFertilizerBalanced(modelResult));
            })
            .catch((error) => {
                console.warn('error:', error);
                dispatch(
                    actions.setFieldErrors({
                        balanceAcidBase: `Fout: ${error.message}`,
                    })
                );
                dispatch(actions.setModelOutputFertilizerBalanced(null));
            })
            .finally(() => {
                setBusy(false);
            });
    };

    return (
        <ModelViewComponent
            modelVersion={modelVersion}
            selectedCrop={selectedCropParameter?.crop || ''}
            selectedStage={selectedStage}
            selectedSubstrate={selectedSubstrate?.substrate || ''}
            dropWaterValue={dropWaterValue}
            ionParameters={ionParameters}
            ionValues={ionValues}
            setIonValue={handleChangeIonValue}
            useDrainAnalysis={useDrainAnalysis}
            modelOutput={modelOutput}
            modelOutputElecCorrect={modelOutputElecCorrect}
            modelOutputFertilizer={modelOutputFertilizer}
            modelOutputFertilizerBalanced={modelOutputFertilizerBalanced}
            visibleOutputColumns={Object.values(outputColumns).filter(
                (c) => c.visible
            )}
            showElecCorrect={showElecCorrect}
            runModel={runModel}
            runElecCorrect={runElecCorrect}
            runFertilizerModel={runFertilizerModel}
            runBalanceAcidBase={runBalanceAcidBase}
            busy={busy}
            alertText={
                fieldErrors ? Object.values(fieldErrors).join(', ') : null
            }
            fertilizerViewOpen={fertilizerViewOpen}
            setFertilizerViewOpen={setFertilizerViewOpen}
        />
    );
};
export default ModelViewController;
